import BudgetAccountComparison from './BudgetAccountComparison';
import React, { useMemo, useState } from 'react';
import './FinancialReviewCard.css';
import './BudgetReviewCard.css';

const currency = value => Number(value || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PASS_LABELS = {
  band: 'Outside TTM band',
  ly_actual: 'vs last year',
  ly_budget: 'vs LY budget',
  carried_anomaly: 'Carried anomaly',
  missing_line: 'Unbudgeted line',
  new_line: 'New line',
  seasonality: 'Seasonality shape',
  rooms_capacity: 'Over capacity',
  rooms_group_otb: 'Group below OTB',
  rooms_group_ly: 'Group vs LY',
  rooms_adr: 'Segment ADR',
  unaddressed_note: 'Note not applied',
};
const SEVERITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low', explained: 'Explained' };
const VERDICT_LABELS = { covered: 'Covered', not_covered: 'Not covered', cannot_determine: 'Cannot determine' };

function fmtValue(finding, value) {
  if (value == null) return '—';
  const isRooms = String(finding.pass || '').startsWith('rooms_');
  if (isRooms && finding.pass !== 'rooms_adr') return Number(value).toLocaleString();
  if (finding.pass === 'seasonality') return `${Number(value).toFixed(1)}%`;
  return currency(value);
}

function Finding({ finding }) {
  const month = finding.month ? MONTH_ABBR[finding.month] : (finding.months?.length === 12 ? 'Annual' : '');
  const delta = finding.delta == null ? null : Number(finding.delta);
  return (
    <div className={`fr-row br-row br-row--${finding.severity}`}>
      <div className="fr-name">
        <strong>{finding.accountNumber ? `${finding.accountNumber} · ` : ''}{finding.accountName}</strong>
        <span>{[finding.department, month].filter(Boolean).join(' · ')}</span>
      </div>
      <span className={`fr-tag br-tag--${finding.severity}`}>{SEVERITY_LABELS[finding.severity] || finding.severity}</span>
      <strong className={`fr-value ${delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''}`}>
        {finding.pass === 'seasonality' ? `${delta > 0 ? '+' : ''}${delta} pts` : `${delta > 0 ? '+' : ''}${fmtValue(finding, delta)}`}
        {finding.deltaPct != null && finding.pass !== 'seasonality' ? <small> {finding.deltaPct > 0 ? '+' : ''}{finding.deltaPct}%</small> : null}
      </strong>
      <span className="fr-reason">
        <em className="br-pass">{PASS_LABELS[finding.pass] || finding.pass}</em>
        {' — '}budget {fmtValue(finding, finding.budgetValue)} vs {finding.comparatorLabel} {fmtValue(finding, finding.comparatorValue)}
        {finding.explanation ? <span className="br-note"> · Explained by note {finding.explanation.noteId}: “{finding.explanation.text}”</span> : null}
        {finding.note ? <span className="br-note"> · Note {finding.note.noteId}: “{finding.note.text}”</span> : null}
      </span>
    </div>
  );
}

function Section({ title, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="br-section">
      <button type="button" className="br-section-head" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <span>{title}</span>
        <small>{count}</small>
        <span className="br-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open ? children : null}
    </div>
  );
}

export default function BudgetReviewCard({ artifact }) {
  const data = artifact?.data || {};
  const summary = data.summary || {};
  const findings = data.findings || [];
  const criteria = data.criteria || [];
  const notesApplied = data.notesApplied || [];
  const unaddressed = data.unaddressedNotes || [];
  const bySeverity = summary.findingsBySeverity || {};
  const [showAll, setShowAll] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of findings) {
      const key = f.department || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [findings]);
  const visibleGroups = showAll ? grouped : grouped.slice(0, 4);
  const totals = summary.budgetTotals || {};
  const ly = summary.lyTotals || {};
  const coverage = summary.coverage || {};

  return (
    <div className="fr-card br-card">
      <div className="fr-coverage">
        <span className="fr-mark" />
        <div>
          <strong>{data.year} budget · {data.version?.name || 'version'} ({data.version?.status || '—'})</strong>
          <span>{summary.accountsReviewed || 0} accounts × {summary.monthsReviewed || 12} months reviewed · cutoff {data.cutoff}{coverage.ttm === false ? ' · TTM history unavailable' : ''}</span>
        </div>
      </div>
      <div className="fr-summary">
        <div><span>Budget revenue</span><strong>{currency(totals.revenue)}</strong><small>{ly.revenue != null ? `LY ${currency(ly.revenue)}` : 'Selected version'}</small></div>
        <div><span>Budget expense</span><strong>{currency(totals.expense)}</strong><small>{ly.expense != null ? `LY ${currency(ly.expense)}` : 'Selected version'}</small></div>
        <div><span>{data.sourceCoverage ? 'Source checks' : 'Findings'}</span><strong>{data.sourceCoverage?.length ?? summary.totalFindings ?? findings.length}</strong><small>{data.sourceCoverage ? `${data.sourceCoverageCounts?.proposed || 0} proposed · ${(data.sourceCoverageCounts?.clarification || 0) + (data.sourceCoverageCounts?.unmapped || 0)} unresolved` : `${bySeverity.high || 0} high · ${bySeverity.medium || 0} medium · ${bySeverity.explained || 0} explained`}</small></div>
      </div>
      <div className="fr-tieout">
        <span className="fr-mark" />
        <span>{coverage.attachments ? `${coverage.attachments} attachment(s): ${coverage.notes || 0} notes, ${coverage.criteria || 0} checklist items` : 'No notes or checklist attached — attach an anomaly log or audit checklist to include them'}</span>
        <strong>{summary.shown < summary.totalFindings ? `top ${summary.shown} of ${summary.totalFindings}` : ''}</strong>
      </div>

      {data.sourceCoverage && <>
        {data.warnings?.map((warning, i) => <p className="fr-foot" key={i}>{warning}</p>)}
        <Section title="Source checks" count={Object.entries(data.sourceCoverageCounts || {}).map(([status, count]) => `${count} ${status}`).join(' · ')} defaultOpen={!data.comparisons?.length}>
          {['clarification', 'unmapped', 'proposed', 'verified', 'historical', 'reference'].map(status => {
            const checks = data.sourceCoverage.filter(c => c.status === status);
            const groups = [...checks.reduce((groups, check) => { const key = `${check.fileName}:${check.id}`; if (!groups.has(key)) groups.set(key, []); groups.get(key).push(check); return groups; }, new Map()).values()];
            return checks.length ? <Section key={status} title={status === 'clarification' ? 'Needs clarification' : status[0].toUpperCase() + status.slice(1)} count={`${groups.length} source items`} defaultOpen={status === 'clarification' || status === 'unmapped'}>
              {groups.map(group => { const c = group[0]; return <div className="fr-row br-source-check" key={`${c.fileName}-${c.id}`}>
                <strong>{group.length === 1 ? c.accountName || c.section : `${c.section} · ${group.length} account checks`}</strong>
                <span>{c.fileName} · {c.section} · {c.id}</span>
                {group.length === 1 ? <p>{c.reason}</p> : <details><summary>Account-specific findings</summary>{group.map((check, i) => <p key={i}><strong>{check.accountName}: </strong>{check.reason}</p>)}</details>}
                <details><summary>Source text</summary><p>{c.text}</p></details>
              </div>; })}
            </Section> : null;
          })}
        </Section>
        <Section title="Calculated account comparisons" count={data.comparisons?.length || 0}>
          {data.comparisons?.map(c => <div key={c.chartOfAccountId}><strong>{c.accountNumber} · {c.accountName} · {c.status}</strong><BudgetAccountComparison comparison={c} /></div>)}
        </Section>
      </>}

      {criteria.length > 0 && (
        <Section title="Checklist" count={`${criteria.filter(c => c.verdict === 'covered').length}/${criteria.length} covered`} defaultOpen>
          <div className="fr-list">
            {criteria.map(c => (
              <div className="fr-row br-row" key={c.id}>
                <div className="fr-name"><strong>{c.id} · {c.text}</strong><span>{c.scopeHint || c.source || ''}</span></div>
                <span className={`fr-tag br-verdict--${c.verdict}`}>{VERDICT_LABELS[c.verdict] || c.verdict}</span>
                <strong className="fr-value">{c.evidenceIds?.length ? c.evidenceIds.join(', ') : ''}</strong>
                {c.note ? <span className="fr-reason">{c.note}</span> : null}
              </div>
            ))}
          </div>
        </Section>
      )}

      {!data.sourceCoverage && <Section title="Findings by department" count={findings.length} defaultOpen>
        {visibleGroups.map(([dept, rows]) => (
          <div key={dept} className="br-group">
            <div className="br-group-head"><span>{dept}</span><small>{rows.length}</small></div>
            <div className="fr-list">
              {rows.map(f => <Finding key={f.id} finding={f} />)}
            </div>
          </div>
        ))}
        {grouped.length > 4 && (
          <button className="fr-more" type="button" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Show fewer departments' : `Show ${grouped.length - 4} more departments`}
          </button>
        )}
        {findings.length === 0 && <div className="fr-foot">No line cleared the materiality gates.</div>}
      </Section>}

      {(notesApplied.length > 0 || unaddressed.length > 0) && (
        <Section title="Notes" count={`${notesApplied.length} linked · ${unaddressed.length} unresolved`}>
          <div className="fr-list">
            {notesApplied.map(n => (
              <div className="fr-row br-row" key={`a-${n.noteId}`}>
                <div className="fr-name"><strong>{n.noteId} · {n.period || ''} {n.kind ? `· ${n.kind.replace('_', ' ')}` : ''}</strong><span>{(n.accounts || []).map(a => a.accountName).join(', ')}</span></div>
                <span className="fr-tag br-tag--explained">{n.findingCount ? `Related to ${n.findingCount}` : 'Noted'}</span>
                <span className="fr-value" />
                <span className="fr-reason">{n.text}</span>
              </div>
            ))}
            {unaddressed.map(n => (
              <div className="fr-row br-row" key={`u-${n.noteId}`}>
                <div className="fr-name"><strong>{n.noteId} · {n.period || ''}</strong><span>{n.reason === 'account_not_identified' ? 'account not identified' : 'carried into the budget despite the note'}</span></div>
                <span className={`fr-tag ${n.reason === 'carried_despite_note' ? 'br-tag--high' : ''}`}>{n.reason === 'carried_despite_note' ? 'Not applied' : 'Unmatched'}</span>
                <span className="fr-value" />
                <span className="fr-reason">{n.text}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="fr-foot">{data.sourceCoverage ? 'No budget values were changed. Review the proposed driver inputs and source assumptions before approving. Every explicit amount is checked without a materiality cutoff.' : <>Read-only review: nothing was written to the budget. Thresholds — |Δ| ≥ {currency(data.thresholds?.minAbsDelta)} and ≥ {data.thresholds?.minPctDelta}%, band ±{data.thresholds?.sigmaBand}σ.</>}</div>
    </div>
  );
}
